import { useEffect, useMemo, useRef, useState, type ChangeEvent, type JSX, type RefObject } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Check,
  ChevronRight,
  ClipboardCheck,
  Download,
  Eye,
  ExternalLink,
  FileSearch,
  FileText,
  FolderOpen,
  MessagesSquare,
  LoaderCircle,
  Mail,
  NotebookPen,
  PanelRightOpen,
  Save,
  ShieldCheck,
  Sparkles
} from "lucide-react";

import type {
  ActivityEventRow,
  CasePage,
  CaseReadiness,
  CaseRow,
  FeasibilityRow,
  FileTreeNode,
  ImportFileRow,
  JobLogRow,
  JobRow,
  PlanRow,
  ProposalRow,
  QuestionAnswerDraft,
  QuestionRow
} from "@/features/app/types";
import {
  buildFileTree,
  buildImportViewerFiles,
  clampProgress,
  buildQuestionAnswerDraft,
  CUSTOM_QUESTION_OPTION,
  formatDuration,
  formatCompactTimestamp,
  formatTimestamp,
  getCaseStateBadgeClassName,
  getCaseStateLabel,
  getCaseSubStateLabel,
  getDefaultExpandedActivityJobId,
  groupJobLogsByJobId,
  hasQuestionAnswerValue,
  hasSavedQuestionAnswer,
  importStatusBadgeClass,
  isActiveJobStatus,
  getImportStatusLabel,
  getJobStatusBadgeClassName,
  getJobStatusLabel,
  getPlanStatusBadgeClassName,
  getPlanStatusLabel,
  parseJsonArrayString
} from "@/features/app/utils";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MarkdownEditor } from "@/components/markdown-editor";
import { Textarea } from "@/components/ui/textarea";
import {
  buildActivityTranscriptEvents,
  findLatestDedupedCtaEventIds,
  getActivityEventDisplayTime,
  getStandaloneActivityJobs,
  type CombinedActivityEvent
} from "@/features/cases/activity-transcript";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Cases list (unchanged)
// ---------------------------------------------------------------------------

type CasesListPageProps = {
  cases: CaseRow[];
  selectedCaseIds: string[];
  statusMessage: string;
  importTenderInputRef: RefObject<HTMLInputElement | null>;
  onImportZip: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  onOpenCase: (caseId: string) => void;
  onToggleCaseSelection: (caseId: string, checked: boolean) => void;
  onToggleSelectVisibleCases: (caseIds: string[], checked: boolean) => void;
  isDeleteCasesDialogOpen: boolean;
  onSetDeleteCasesDialogOpen: (open: boolean) => void;
  isDeletingCases: boolean;
  onDeleteSelectedCases: () => Promise<void>;
};

export const CasesListPage = ({
  cases,
  selectedCaseIds,
  statusMessage,
  importTenderInputRef,
  onImportZip,
  onOpenCase,
  onToggleCaseSelection,
  onToggleSelectVisibleCases,
  isDeleteCasesDialogOpen,
  onSetDeleteCasesDialogOpen,
  isDeletingCases,
  onDeleteSelectedCases
}: CasesListPageProps): JSX.Element => {
  const [query, setQuery] = useState("");
  const [stateFilter, setStateFilter] = useState<string>("all");

  const filteredCases = useMemo(() => {
    return cases.filter((caseRow) => {
      const matchesQuery =
        query.trim().length === 0 || caseRow.title.toLowerCase().includes(query.trim().toLowerCase());
      const matchesState = stateFilter === "all" || caseRow.state === stateFilter;
      return matchesQuery && matchesState;
    });
  }, [cases, query, stateFilter]);

  const visibleCaseIds = filteredCases.map((caseRow) => caseRow.id);
  const selectedVisibleCaseIds = visibleCaseIds.filter((caseId) => selectedCaseIds.includes(caseId));
  const areAllVisibleCasesSelected =
    visibleCaseIds.length > 0 && selectedVisibleCaseIds.length === visibleCaseIds.length;
  const areSomeVisibleCasesSelected = selectedVisibleCaseIds.length > 0 && !areAllVisibleCasesSelected;
  const stateOptions = ["all", ...Array.from(new Set(cases.map((caseRow) => caseRow.state))).sort()];

  return (
    <div className="space-y-4">
      <input
        ref={importTenderInputRef}
        type="file"
        accept=".zip"
        onChange={(event) => void onImportZip(event)}
        className="sr-only"
      />

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold tracking-tight">Proposals</h2>
          {statusMessage ? <Badge variant="secondary">{statusMessage}</Badge> : null}
        </div>
        <div className="flex items-center gap-2">
          {selectedCaseIds.length > 0 ? (
            <Button variant="destructive" size="sm" onClick={() => onSetDeleteCasesDialogOpen(true)}>
              Delete {selectedCaseIds.length} selected
            </Button>
          ) : null}
          <Button onClick={() => importTenderInputRef.current?.click()}>
            <Sparkles className="size-4" />
            Import tender
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="max-w-xs"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search proposals..."
        />
        {stateOptions.length > 1 ? (
          <Tabs value={stateFilter} onValueChange={setStateFilter}>
            <TabsList>
              {stateOptions.map((stateOption) => (
                <TabsTrigger key={stateOption} value={stateOption}>
                  {stateOption === "all" ? "All" : getCaseStateLabel(stateOption)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : null}
      </div>

      {selectedCaseIds.length > 0 ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/40 px-4 py-2.5">
          <p className="text-muted-foreground text-sm">
            {selectedCaseIds.length} proposal{selectedCaseIds.length > 1 ? "s" : ""} selected
          </p>
          <Button variant="ghost" size="sm" onClick={() => onToggleSelectVisibleCases(visibleCaseIds, false)}>
            Clear selection
          </Button>
        </div>
      ) : null}

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={areAllVisibleCasesSelected ? true : areSomeVisibleCasesSelected ? "indeterminate" : false}
                  onCheckedChange={(checked) => onToggleSelectVisibleCases(visibleCaseIds, checked === true)}
                    aria-label="Select all visible proposals"
                />
              </TableHead>
              <TableHead>Proposal</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Activity</TableHead>
              <TableHead className="text-right">Progress</TableHead>
              <TableHead className="text-right">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground py-16 text-center">
                  {cases.length === 0 ? "No proposals yet. Import a tender to get started." : "No proposals match the current filters."}
                </TableCell>
              </TableRow>
            ) : (
              filteredCases.map((caseRow) => (
                <TableRow
                  key={caseRow.id}
                  className="data-[state=selected]:bg-muted/50 cursor-pointer"
                  data-state={selectedCaseIds.includes(caseRow.id) ? "selected" : "unselected"}
                  onClick={() => onOpenCase(caseRow.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onOpenCase(caseRow.id);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                    aria-label={`Open proposal ${caseRow.title}`}
                >
                  <TableCell onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                    <Checkbox
                      checked={selectedCaseIds.includes(caseRow.id)}
                      onCheckedChange={(checked) => onToggleCaseSelection(caseRow.id, checked === true)}
                      aria-label={`Select proposal ${caseRow.title}`}
                    />
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{caseRow.title}</p>
                    {caseRow.sub_state ? <p className="text-muted-foreground mt-0.5 text-xs">{getCaseSubStateLabel(caseRow.sub_state)}</p> : null}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getCaseStateBadgeClassName(caseRow.state)}>
                      {getCaseStateLabel(caseRow.state)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {caseRow.running_job ? `${caseRow.running_job.kind} (${getJobStatusLabel(caseRow.running_job.status)})` : "Idle"}
                  </TableCell>
                  <TableCell className="text-right">
                    {caseRow.running_job ? (
                      <div className="ml-auto w-32 space-y-1">
                        <Progress value={clampProgress(caseRow.running_job.progress)} />
                        <p className="text-muted-foreground text-xs tabular-nums">{caseRow.running_job.progress}%</p>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-right text-sm">
                    {formatTimestamp(caseRow.created_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={isDeleteCasesDialogOpen} onOpenChange={onSetDeleteCasesDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete selected proposals?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected proposals, including imported files and generated outputs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingCases}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={(event) => {
                event.preventDefault();
                void onDeleteSelectedCases();
              }}
              disabled={isDeletingCases || selectedCaseIds.length === 0}
            >
              {isDeletingCases ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Proposal flow stepper
// ---------------------------------------------------------------------------

type FlowStep = { label: string; status: "done" | "active" | "upcoming" };

const getFlowSteps = (
  state: string,
  subState: string | null,
  caseFeasibility: FeasibilityRow | null,
  caseQuestions: QuestionRow[],
  answeredQuestions: number,
  casePlan: PlanRow | null,
  caseProposals: ProposalRow[]
): FlowStep[] => {
  const importDone = state !== "importing" && state !== "import_failed";
  const questionsDone = caseQuestions.length > 0 && answeredQuestions === caseQuestions.length;
  const outlineNeedsRefresh = state === "questions_generated" && casePlan !== null;
  const outlineDone = casePlan?.status === "approved" && !outlineNeedsRefresh;
  const proposalDone = caseProposals.some(p => p.status === "succeeded");

  const activeState = (() => {
    if (!importDone) return "import";
    if (state === "context_ready" || (state === "questions_generated" && !questionsDone)) return "questions";
    if (state === "questions_generated" || state === "plan_generated") return "outline";
    return "proposal";
  })();

  return [
    { label: "Import", status: importDone ? "done" : activeState === "import" ? "active" : "upcoming" },
    { label: "Questions", status: questionsDone ? "done" : activeState === "questions" ? "active" : "upcoming" },
    { label: "Outline", status: outlineDone ? "done" : activeState === "outline" ? "active" : "upcoming" },
    { label: "Proposal", status: proposalDone ? "done" : activeState === "proposal" ? "active" : "upcoming" }
  ];
};

const getNextStepMessage = (state: string, subState: string | null, hasRunningJob: boolean): string | null => {
  if (hasRunningJob) return null;
  switch (state) {
    case "importing": return "Importing tender documents…";
    case "import_failed": return "Import failed — check chat details.";
    case "context_ready": return "Generate steering questions to begin building your proposal.";
    case "questions_generated":
      return subState === "awaiting_answers"
        ? "Review and answer the steering questions, then generate an outline."
        : "Questions answered — generate the proposal outline.";
    case "plan_generated": return "Review the outline and approve it to proceed.";
    case "plan_approved": return "Outline approved — generate the full proposal document.";
    case "proposal_generated": return "Generating the proposal PDF…";
    case "proposal_document_generated": return "Proposal ready — download or review the PDF.";
    default: return null;
  }
};

const FlowStepper = ({ steps }: { steps: FlowStep[] }) => (
  <div className="flex items-center gap-1">
    {steps.map((step, i) => (
      <div key={step.label} className="flex items-center gap-1">
        {i > 0 ? <ChevronRight className="size-3 text-muted-foreground/50 shrink-0" /> : null}
        <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
          step.status === "done"
            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
            : step.status === "active"
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground/60"
        }`}>
          {step.status === "done" ? <Check className="size-3" /> : null}
          {step.status === "active" ? <span className="size-1.5 rounded-full bg-primary animate-pulse" /> : null}
          {step.label}
        </div>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Primary CTA logic
// ---------------------------------------------------------------------------

type PrimaryCTAConfig = {
  label: string;
  action: () => void;
  disabled: boolean;
  variant: "default" | "outline";
  icon: JSX.Element;
};

const getPrimaryCTA = (
  state: string,
  subState: string | null,
  hasRunningJob: boolean,
  caseProposals: ProposalRow[],
  caseReadiness: CaseReadiness,
  actions: {
    generateQuestions: () => void;
    openQuestions: () => void;
    generatePlan: () => void;
    openPlan: () => void;
    generateProposal: () => void;
    openLatestPdf: () => void;
    openActivity: () => void;
  }
): PrimaryCTAConfig => {
  if (hasRunningJob) {
    return { label: "Running...", action: () => {}, disabled: true, variant: "outline", icon: <LoaderCircle className="size-4 animate-spin" /> };
  }
  switch (state) {
    case "importing":
      return { label: "Importing...", action: () => {}, disabled: true, variant: "outline", icon: <LoaderCircle className="size-4 animate-spin" /> };
    case "import_failed":
      return { label: "View Error Log", action: actions.openActivity, disabled: false, variant: "outline", icon: <AlertCircle className="size-4" /> };
    case "context_ready":
      return { label: "Generate Questions", action: actions.generateQuestions, disabled: !caseReadiness.canGenerateQuestions, variant: "default", icon: <Sparkles className="size-4" /> };
    case "questions_generated":
      if (subState === "awaiting_answers") {
        return { label: "Answer Questions", action: actions.openQuestions, disabled: false, variant: "default", icon: <ClipboardCheck className="size-4" /> };
      }
      return { label: "Generate Outline", action: actions.generatePlan, disabled: !caseReadiness.canGeneratePlan, variant: "default", icon: <Sparkles className="size-4" /> };
    case "plan_generated":
      return { label: "Review & Approve Outline", action: actions.openPlan, disabled: false, variant: "default", icon: <ShieldCheck className="size-4" /> };
    case "plan_approved":
      if (subState === "needs_regeneration") {
        return { label: "Regenerate Proposal", action: actions.generateProposal, disabled: !caseReadiness.canGenerateProposal, variant: "default", icon: <NotebookPen className="size-4" /> };
      }
      return { label: "Generate Proposal", action: actions.generateProposal, disabled: !caseReadiness.canGenerateProposal, variant: "default", icon: <NotebookPen className="size-4" /> };
    case "proposal_generated":
    case "proposal_document_generated":
      if (caseProposals.length > 0) {
        return { label: "View Proposal PDF", action: actions.openLatestPdf, disabled: false, variant: "default", icon: <ExternalLink className="size-4" /> };
      }
      return { label: "Generate Proposal", action: actions.generateProposal, disabled: !caseReadiness.canGenerateProposal, variant: "default", icon: <NotebookPen className="size-4" /> };
    default:
      return { label: "View Overview", action: () => {}, disabled: false, variant: "outline", icon: <Eye className="size-4" /> };
  }
};

const getDefaultTab = (state: string, subState: string | null): CasePage => {
  switch (state) {
    case "importing":
    case "import_failed":
    case "context_ready":
      return "overview";
    case "questions_generated":
      return "questions";
    case "plan_generated":
      return "plan";
    case "plan_approved":
      return subState === "needs_regeneration" ? "proposal" : "plan";
    case "proposal_generated":
    case "proposal_document_generated":
      return "proposal";
    default:
      return "overview";
  }
};

// ---------------------------------------------------------------------------
// Tab definitions with completion indicators
// ---------------------------------------------------------------------------

type TabDef = { id: CasePage; label: string; badge?: string; done?: boolean; alert?: boolean };

const buildTabs = (
  state: string,
  caseQuestions: QuestionRow[],
  answeredQuestions: number,
  casePlan: PlanRow | null,
  caseProposals: ProposalRow[],
  caseFeasibility: FeasibilityRow | null,
  importViewerFilesCount: number
): TabDef[] => {
  const outlineNeedsRefresh = state === "questions_generated" && casePlan !== null;
  return [
    { id: "overview", label: "Overview", done: caseFeasibility !== null },
    {
      id: "questions",
      label: "Steering Questions",
      badge: caseQuestions.length > 0 ? `${answeredQuestions}/${caseQuestions.length}` : undefined,
      done: caseQuestions.length > 0 && answeredQuestions === caseQuestions.length
    },
    {
      id: "plan",
      label: "Outline",
      badge: casePlan ? (casePlan.status === "approved" ? undefined : "draft") : undefined,
      done: casePlan?.status === "approved" && !outlineNeedsRefresh
    },
    {
      id: "proposal",
      label: "Proposal",
      badge: caseProposals.length > 0 ? `v${caseProposals[0].version}` : undefined,
      done: caseProposals.some((p) => p.status === "succeeded")
    },
    { id: "files", label: "Tender Documents", badge: importViewerFilesCount > 0 ? String(importViewerFilesCount) : undefined }
  ];
};

// ---------------------------------------------------------------------------
// CaseWorkspacePage (redesigned)
// ---------------------------------------------------------------------------

type CaseWorkspacePageProps = {
  selectedCase: CaseRow | null;
  selectedCaseId: string | null;
  selectedCasePage: CasePage;
  onSelectPage: (page: CasePage) => void;
  onBackToCases: () => void;
  statusMessage: string;
  caseJobs: JobRow[];
  caseLogs: JobLogRow[];
  activityEvents: ActivityEventRow[];
  caseQuestions: QuestionRow[];
  casePlan: PlanRow | null;
  caseProposals: ProposalRow[];
  caseImportFiles: ImportFileRow[];
  caseFeasibility: FeasibilityRow | null;
  caseReadiness: CaseReadiness;
  selectedImportLogJobId: string | null;
  selectedImportLogFiles: ImportFileRow[];
  onToggleImportLogDetails: (jobId: string) => Promise<void>;
  notesDraft: string;
  onChangeNotesDraft: (value: string) => void;
  onSaveCaseMeta: () => Promise<void>;
  autoSaveNotes: (notes: string) => Promise<void>;
  onGenerateQuestions: () => Promise<void>;
  onGeneratePlan: () => Promise<void>;
  onRunProposal: () => Promise<void>;
  questionAnswerDrafts: Record<string, QuestionAnswerDraft>;
  onUpdateQuestionAnswer: (questionId: string, key: "selectedOption" | "freeText", value: string) => void;
  onSaveAnswer: (questionId: string) => Promise<void>;
  autoSaveAnswer: (questionId: string, draft: QuestionAnswerDraft) => Promise<void>;
  planMarkdown: string;
  onChangePlanMarkdown: (value: string) => void;
  onSavePlan: (status: "draft" | "approved") => Promise<void>;
  proposalNotes: string;
  onChangeProposalNotes: (value: string) => void;
  proposalFeedback: string;
  onChangeProposalFeedback: (value: string) => void;
  proposalChatDraft: string;
  onChangeProposalChatDraft: (value: string) => void;
  onRegeneratePdf: (version: number) => Promise<void>;
  onOpenPdf: (version: number) => void;
  onDownloadPdf: (version: number) => void;
  onDummySend: () => Promise<void>;
  onSendProposalChatMessage: () => Promise<void>;
  selectedImportFilePath: string;
  onSelectImportFile: (filePath: string) => void;
  importViewerMarkdownContent: string;
  importViewerError: string;
  isImportViewerLoading: boolean;
};

export const CaseWorkspacePage = ({
  selectedCase,
  selectedCaseId,
  selectedCasePage,
  onSelectPage,
  onBackToCases,
  statusMessage,
  caseJobs,
  caseLogs,
  activityEvents,
  caseQuestions,
  casePlan,
  caseProposals,
  caseImportFiles,
  caseFeasibility,
  caseReadiness,
  selectedImportLogJobId,
  selectedImportLogFiles,
  onToggleImportLogDetails,
  notesDraft,
  onChangeNotesDraft,
  onSaveCaseMeta,
  autoSaveNotes,
  onGenerateQuestions,
  onGeneratePlan,
  onRunProposal,
  questionAnswerDrafts,
  onUpdateQuestionAnswer,
  onSaveAnswer,
  autoSaveAnswer,
  planMarkdown,
  onChangePlanMarkdown,
  onSavePlan,
  proposalNotes,
  onChangeProposalNotes,
  proposalFeedback,
  onChangeProposalFeedback,
  proposalChatDraft,
  onChangeProposalChatDraft,
  onRegeneratePdf,
  onOpenPdf,
  onDownloadPdf,
  onDummySend,
  onSendProposalChatMessage,
  selectedImportFilePath,
  onSelectImportFile,
  importViewerMarkdownContent,
  importViewerError,
  isImportViewerLoading
}: CaseWorkspacePageProps): JSX.Element => {
  const [isEvidenceDrawerOpen, setIsEvidenceDrawerOpen] = useState(false);
  const [lastNonActivityPage, setLastNonActivityPage] = useState<CasePage>("overview");
  const isMobile = useIsMobile();

  const answeredQuestions = caseQuestions.filter((question) => hasSavedQuestionAnswer(question.answer)).length;
  const importViewerFiles = useMemo(() => buildImportViewerFiles(caseImportFiles), [caseImportFiles]);
  const importViewerFileTree = useMemo(() => buildFileTree(importViewerFiles), [importViewerFiles]);
  const hasRunningJob = selectedCase?.running_job != null && isActiveJobStatus(selectedCase.running_job.status);
  const runningJob = hasRunningJob ? selectedCase!.running_job! : null;
  const { logsByJobId: activityLogsByJobId } = useMemo(() => groupJobLogsByJobId(caseLogs), [caseLogs]);
  const runningJobDetails = useMemo(
    () => (runningJob ? caseJobs.find((job) => job.id === runningJob.id) ?? null : null),
    [caseJobs, runningJob]
  );
  const runningJobLogs = useMemo(
    () => (runningJob ? activityLogsByJobId.get(runningJob.id) ?? [] : []),
    [activityLogsByJobId, runningJob]
  );

  const primaryCTA = getPrimaryCTA(
    selectedCase?.state ?? "",
    selectedCase?.sub_state ?? null,
    hasRunningJob,
    caseProposals,
    caseReadiness,
    {
      generateQuestions: () => void onGenerateQuestions(),
      openQuestions: () => onSelectPage("questions"),
      generatePlan: () => void onGeneratePlan(),
      openPlan: () => onSelectPage("plan"),
      generateProposal: () => void onRunProposal(),
      openLatestPdf: () => { if (caseProposals.length > 0) onOpenPdf(caseProposals[0].version); },
      openActivity: () => onSelectPage("activity")
    }
  );

  const flowSteps = getFlowSteps(
    selectedCase?.state ?? "",
    selectedCase?.sub_state ?? null,
    caseFeasibility,
    caseQuestions,
    answeredQuestions,
    casePlan,
    caseProposals
  );
  const nextStepMessage = getNextStepMessage(selectedCase?.state ?? "", selectedCase?.sub_state ?? null, hasRunningJob);

  const tabs = buildTabs(
    selectedCase?.state ?? "",
    caseQuestions,
    answeredQuestions,
    casePlan,
    caseProposals,
    caseFeasibility,
    importViewerFiles.length
  );
  const outlineNeedsRefresh = selectedCase?.state === "questions_generated" && casePlan !== null;
  const fallbackActivityReturnPage =
    caseProposals.length > 0
      ? "proposal"
      : casePlan
        ? "plan"
        : caseQuestions.length > 0
          ? "questions"
          : "overview";
  const activityReturnPage = lastNonActivityPage === "activity" ? fallbackActivityReturnPage : lastNonActivityPage;
  const workspacePage = selectedCasePage === "activity" ? activityReturnPage : selectedCasePage;
  const activityReturnLabel = tabs.find((tab) => tab.id === activityReturnPage)?.label ?? "Proposal";

  useEffect(() => {
    if (selectedCasePage !== "activity") {
      setLastNonActivityPage(selectedCasePage);
    }
  }, [selectedCasePage]);

  const pageContent = (() => {
    switch (workspacePage) {
      case "overview":
        return (
          <OverviewTab
            selectedCase={selectedCase}
            caseFeasibility={caseFeasibility}
            caseQuestions={caseQuestions}
            caseReadiness={caseReadiness}
            answeredQuestions={answeredQuestions}
            importViewerFilesCount={importViewerFiles.length}
          />
        );
      case "files":
        return (
          <EvidenceTab
            selectedCaseId={selectedCaseId}
            importViewerFileTree={importViewerFileTree}
            selectedImportFilePath={selectedImportFilePath}
            onSelectImportFile={onSelectImportFile}
            importViewerMarkdownContent={importViewerMarkdownContent}
            importViewerError={importViewerError}
            isImportViewerLoading={isImportViewerLoading}
          />
        );
      case "questions":
        return (
          <QuestionsTab
            caseQuestions={caseQuestions}
            answeredQuestions={answeredQuestions}
            onGenerateQuestions={onGenerateQuestions}
            onContinueToPlan={async () => {
              onSelectPage("plan");
              await onGeneratePlan();
            }}
            questionAnswerDrafts={questionAnswerDrafts}
            onUpdateQuestionAnswer={onUpdateQuestionAnswer}
            autoSaveAnswer={autoSaveAnswer}
            onOpenEvidenceDrawer={() => setIsEvidenceDrawerOpen(true)}
            notesDraft={notesDraft}
            onChangeNotesDraft={onChangeNotesDraft}
            autoSaveNotes={autoSaveNotes}
          />
        );
      case "plan":
        return (
          <PlanTab
            casePlan={casePlan}
            caseReadiness={caseReadiness}
            outlineNeedsRefresh={outlineNeedsRefresh}
            planMarkdown={planMarkdown}
            onChangePlanMarkdown={onChangePlanMarkdown}
            onGeneratePlan={onGeneratePlan}
            onSavePlan={onSavePlan}
            onBackToQuestions={() => onSelectPage("questions")}
            onOpenEvidenceDrawer={() => setIsEvidenceDrawerOpen(true)}
          />
        );
      case "proposal":
        return (
          <ProposalTab
            selectedCase={selectedCase}
            casePlan={casePlan}
            caseProposals={caseProposals}
            proposalNotes={proposalNotes}
            onChangeProposalNotes={onChangeProposalNotes}
            proposalFeedback={proposalFeedback}
            onChangeProposalFeedback={onChangeProposalFeedback}
            onRunProposal={onRunProposal}
            onRegeneratePdf={onRegeneratePdf}
            onOpenPdf={onOpenPdf}
            onDownloadPdf={onDownloadPdf}
            onDummySend={onDummySend}
            caseReadiness={caseReadiness}
            onBackToPlan={() => onSelectPage("plan")}
          />
        );
      default:
        return null;
    }
  })();

  const activityOverlayContent = (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="border-b px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground">/</span>
          <Button variant="ghost" size="sm" onClick={() => onSelectPage(activityReturnPage)}>
            <ArrowLeft className="size-4" />
            Back to {activityReturnLabel}
          </Button>
          <div className="min-w-0">
            <p className="text-sm font-medium">Proposal chat</p>
            <p className="text-muted-foreground truncate text-sm">{selectedCase?.title ?? "Untitled Proposal"}</p>
          </div>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden px-4 py-4 sm:px-6 sm:py-6">
        <ActivityTab
          caseJobs={caseJobs}
          caseLogs={caseLogs}
          activityEvents={activityEvents}
          caseFeasibility={caseFeasibility}
          casePlan={casePlan}
          selectedImportLogJobId={selectedImportLogJobId}
          selectedImportLogFiles={selectedImportLogFiles}
          onToggleImportLogDetails={onToggleImportLogDetails}
          onGenerateQuestions={onGenerateQuestions}
          onOpenQuestions={() => onSelectPage("questions")}
          onGeneratePlan={onGeneratePlan}
          onOpenPlan={() => onSelectPage("plan")}
          onRunProposal={onRunProposal}
          caseReadiness={caseReadiness}
          selectedCase={selectedCase}
          caseProposals={caseProposals}
          proposalChatDraft={proposalChatDraft}
          onChangeProposalChatDraft={onChangeProposalChatDraft}
          onSendProposalChatMessage={onSendProposalChatMessage}
          onOpenPdf={onOpenPdf}
          onDownloadPdf={onDownloadPdf}
          isFullscreen
        />
      </div>
    </div>
  );
  const activityOverlay = selectedCasePage === "activity" ? (
    isMobile ? (
    <Dialog open onOpenChange={(open) => !open && onSelectPage(activityReturnPage)}>
      <DialogContent className="top-0 left-0 h-screen w-screen max-w-none translate-x-0 translate-y-0 gap-0 rounded-none border-0 p-0 sm:max-w-none">
        {activityOverlayContent}
      </DialogContent>
    </Dialog>
    ) : (
      <div data-testid="activity-overlay" className="absolute inset-0 z-40 overflow-hidden bg-background">
        {activityOverlayContent}
      </div>
    )
  ) : null;

  return (
    <div className="relative flex h-full min-h-0 flex-col gap-4">
      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBackToCases}>
          <ArrowLeft className="size-4" />
          Proposals
        </Button>
        <span className="text-muted-foreground">/</span>
        <h2 className="truncate text-lg font-semibold tracking-tight">{selectedCase?.title ?? "Untitled Proposal"}</h2>
        {selectedCasePage === "activity" ? null : (
          <Button
            variant="outline"
            size="sm"
            className="ml-2 rounded-full"
            onClick={() => onSelectPage("activity")}
          >
            <MessagesSquare className="size-4" />
            Chat
          </Button>
        )}
        {statusMessage ? <Badge variant="secondary" className="ml-auto">{statusMessage}</Badge> : null}
      </div>

      {/* Case Header */}
      <Card>
        <CardContent className="space-y-4 p-4 sm:p-6">
          {/* Flow stepper + metadata */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <FlowStepper steps={flowSteps} />
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {caseFeasibility ? <span>Feasibility {caseFeasibility.score}/100</span> : null}
              {selectedCase?.workflow_language ? <span>{selectedCase.workflow_language}</span> : null}
              <span>{importViewerFiles.length} doc{importViewerFiles.length !== 1 ? "s" : ""}</span>
            </div>
          </div>

          {/* Running activity */}
          {runningJob ? (
            <div className="overflow-hidden rounded-xl border border-primary/20 bg-muted/20">
              <div className="flex items-start gap-3 px-4 py-3">
                <div className={cn(
                  "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full",
                  getActivityStatusClassName(runningJob.status)
                )}>
                  <LoaderCircle className="size-4 animate-spin" />
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium">{getActivityJobLabel(runningJob.kind)}</p>
                        <Badge variant="outline" className={getJobStatusBadgeClassName(runningJob.status)}>
                          {getJobStatusLabel(runningJob.status)}
                        </Badge>
                        <span className="text-muted-foreground text-xs tabular-nums">{runningJob.progress}%</span>
                      </div>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {formatTimestamp(runningJob.created_at)} · {formatDuration(runningJob.created_at, runningJob.updated_at, runningJob.status)}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => onSelectPage("activity")}>
                      Open chat
                    </Button>
                  </div>
                  <Progress value={clampProgress(runningJob.progress)} className="h-1.5" />
                  {runningJobDetails?.message ? <p className="text-muted-foreground text-xs">{runningJobDetails.message}</p> : null}
                  {runningJobDetails?.error ? <p className="text-destructive text-xs">{runningJobDetails.error}</p> : null}
                </div>
              </div>

              <div className="border-t px-4 py-3">
                <ActivityLogList logs={runningJobLogs} maxHeightClassName="max-h-40" emptyMessage="Waiting for log entries from this step..." />
              </div>
            </div>
          ) : null}

          {/* Next step guidance + primary CTA */}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              variant={primaryCTA.variant}
              disabled={primaryCTA.disabled}
              onClick={primaryCTA.action}
            >
              {primaryCTA.icon}
              {primaryCTA.label}
            </Button>

            {/* Secondary actions */}
            {selectedCase?.state === "proposal_document_generated" && caseProposals.length > 0 ? (
              <>
                <Button variant="outline" size="sm" onClick={() => onDownloadPdf(caseProposals[0].version)}>
                  <Download className="size-4" />
                  Download PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => void onRunProposal()} disabled={!caseReadiness.canGenerateProposal}>
                  <NotebookPen className="size-4" />
                  Regenerate
                </Button>
              </>
            ) : null}
            {nextStepMessage ? (
              <span className="text-sm text-muted-foreground">{nextStepMessage}</span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Tab bar with completion indicators */}
      <Tabs value={workspacePage} onValueChange={(v) => onSelectPage(v as CasePage)}>
        <TabsList variant="line">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="gap-1.5">
              {tab.done ? <Check className="size-3 text-emerald-600" /> : null}
              {tab.alert ? <AlertCircle className="size-3 text-destructive" /> : null}
              {tab.label}
              {tab.badge ? (
                <span className="text-muted-foreground ml-0.5 text-xs">({tab.badge})</span>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Full-width tab content */}
      <div>{pageContent}</div>

      {activityOverlay}

      {/* Evidence drawer (shared) */}
      <Sheet open={isEvidenceDrawerOpen} onOpenChange={setIsEvidenceDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Tender Documents</SheetTitle>
            <SheetDescription>Reference material from the imported tender.</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 px-4 pb-4">
            <EvidenceBrowser
              importViewerFileTree={importViewerFileTree}
              selectedImportFilePath={selectedImportFilePath}
              onSelectImportFile={onSelectImportFile}
            />
            <EvidencePreview
              selectedCaseId={selectedCaseId}
              selectedImportFilePath={selectedImportFilePath}
              importViewerMarkdownContent={importViewerMarkdownContent}
              importViewerError={importViewerError}
              isImportViewerLoading={isImportViewerLoading}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overview Tab
// ---------------------------------------------------------------------------

type OverviewTabProps = {
  selectedCase: CaseRow | null;
  caseFeasibility: FeasibilityRow | null;
  caseQuestions: QuestionRow[];
  caseReadiness: CaseReadiness;
  answeredQuestions: number;
  importViewerFilesCount: number;
};

const getFeasibilityInsights = (
  caseFeasibility: FeasibilityRow | null
): {
  executiveSummary: string;
  pros: string[];
  cons: string[];
} => ({
  executiveSummary:
    caseFeasibility?.executiveSummary?.trim() ||
    "Executive summary not available for this feasibility run yet. Re-run the feasibility analysis to generate one.",
  pros: caseFeasibility ? parseJsonArrayString(caseFeasibility.pros) : [],
  cons: caseFeasibility ? parseJsonArrayString(caseFeasibility.cons) : []
});

const FeasibilityScoreRing = ({ score }: { score: number }) => {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const filled = (score / 100) * circumference;
  const color = score >= 70 ? "text-emerald-600" : score >= 40 ? "text-amber-500" : "text-red-500";

  return (
    <div className="relative flex items-center justify-center">
      <svg width="100" height="100" viewBox="0 0 100 100" className="-rotate-90">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="currentColor" strokeWidth="8" className="text-muted/40" />
        <circle
          cx="50" cy="50" r={radius} fill="none" stroke="currentColor" strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={circumference - filled}
          strokeLinecap="round" className={color}
        />
      </svg>
      <span className="absolute text-2xl font-semibold tabular-nums">{score}</span>
    </div>
  );
};

const FeasibilityInsightPanel = ({
  caseFeasibility,
  className,
  compact = false
}: {
  caseFeasibility: FeasibilityRow;
  className?: string;
  compact?: boolean;
}): JSX.Element => {
  const { executiveSummary, pros, cons } = getFeasibilityInsights(caseFeasibility);

  if (compact) {
    return (
      <Card className={cn("border-black/10 bg-background framna-shell", className)}>
        <CardContent className="space-y-4 px-4 py-4 sm:px-5">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-black/8 pb-3">
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/56">Feasibility</p>
              <p className="max-w-3xl text-sm leading-6 text-black/72">{executiveSummary}</p>
            </div>
            <div className="inline-flex min-w-16 shrink-0 flex-col items-center rounded-2xl border border-[#C5DBDF] bg-[#DEEBED] px-3 py-2 text-center">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#24424A]">Score</span>
              <span className="text-2xl font-semibold tabular-nums text-[#162D33]">{caseFeasibility.score}</span>
            </div>
          </div>

          <div className="grid gap-3 xl:grid-cols-2">
            <section className="rounded-[1.1rem] border border-[#C5DBDF] bg-[#DEEBED]/72 p-3">
              <div className="flex items-center gap-2">
                <div className="size-1.5 rounded-full bg-[#22C153]" />
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#24424A]">Strengths</p>
              </div>
              <ul className="mt-2.5 space-y-2">
                {pros.map((item) => (
                  <li key={item} className="flex gap-2.5 text-sm leading-5 text-[#162D33]">
                    <Check className="mt-0.5 size-3.5 shrink-0 text-[#0F6B2C]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-[1.1rem] border border-[#FDA64B]/45 bg-[#FFF1DF]/90 p-3">
              <div className="flex items-center gap-2">
                <div className="size-1.5 rounded-full bg-[#FDA64B]" />
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A4B00]">Risks</p>
              </div>
              <ul className="mt-2.5 space-y-2">
                {cons.map((item) => (
                  <li key={item} className="flex gap-2.5 text-sm leading-5 text-[#5C3500]">
                    <AlertCircle className="mt-0.5 size-3.5 shrink-0 text-[#8A4B00]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("overflow-hidden border-black/10 bg-background framna-shell", className)}>
      <CardContent className="p-0">
        <div className="grid gap-0 lg:grid-cols-[188px_minmax(0,1fr)]">
          <div className="framna-hero flex flex-col items-center justify-center gap-3 px-6 py-8 text-black lg:min-h-full">
            <span className="rounded-full border border-black/10 bg-white/72 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em]">
              Feasibility
            </span>
            <FeasibilityScoreRing score={caseFeasibility.score} />
            <p className="max-w-[10rem] text-center text-xs font-medium text-black/72">
              Bid viability based on imported case material and company context.
            </p>
          </div>

          <div className="space-y-6 px-6 py-6">
            <div className="space-y-3 border-b border-black/8 pb-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-black/56">Executive Summary</p>
              <div className="space-y-3">
                <h3 className="font-editorial text-3xl leading-none text-black">Tender readout</h3>
                <p className="max-w-3xl text-sm leading-6 text-black/72">{executiveSummary}</p>
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <section className="rounded-[1.35rem] border border-[#C5DBDF] bg-[#DEEBED] p-4">
                <div className="flex items-center gap-2">
                  <div className="size-2 rounded-full bg-[#22C153]" />
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#24424A]">Strengths</p>
                </div>
                <ul className="mt-4 space-y-3">
                  {pros.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-6 text-[#162D33]">
                      <Check className="mt-1 size-4 shrink-0 text-[#0F6B2C]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="rounded-[1.35rem] border border-[#FDA64B]/45 bg-[#FFF1DF] p-4">
                <div className="flex items-center gap-2">
                  <div className="size-2 rounded-full bg-[#FDA64B]" />
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8A4B00]">Risks</p>
                </div>
                <ul className="mt-4 space-y-3">
                  {cons.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-6 text-[#5C3500]">
                      <AlertCircle className="mt-1 size-4 shrink-0 text-[#8A4B00]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const OverviewTab = ({
  selectedCase,
  caseFeasibility,
  caseQuestions,
  answeredQuestions,
  importViewerFilesCount
}: OverviewTabProps) => {
  return (
    <div className="space-y-5">
      {caseFeasibility ? <FeasibilityInsightPanel caseFeasibility={caseFeasibility} /> : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
              <FileSearch className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{importViewerFilesCount}</p>
              <p className="text-sm text-muted-foreground">Tender documents</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
              <ClipboardCheck className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">
                {caseQuestions.length > 0 ? `${answeredQuestions}/${caseQuestions.length}` : "—"}
              </p>
              <p className="text-sm text-muted-foreground">Questions answered</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
              <FileText className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{selectedCase?.workflow_language ?? "—"}</p>
              <p className="text-sm text-muted-foreground">Language</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Questions Tab
// ---------------------------------------------------------------------------

type QuestionsTabProps = {
  caseQuestions: QuestionRow[];
  answeredQuestions: number;
  onGenerateQuestions: () => Promise<void>;
  onContinueToPlan: () => Promise<void>;
  questionAnswerDrafts: Record<string, QuestionAnswerDraft>;
  onUpdateQuestionAnswer: (questionId: string, key: "selectedOption" | "freeText", value: string) => void;
  autoSaveAnswer: (questionId: string, draft: QuestionAnswerDraft) => Promise<void>;
  onOpenEvidenceDrawer: () => void;
  notesDraft: string;
  onChangeNotesDraft: (value: string) => void;
  autoSaveNotes: (notes: string) => Promise<void>;
};

export const getUnsavedQuestionDrafts = (
  caseQuestions: QuestionRow[],
  questionAnswerDrafts: Record<string, QuestionAnswerDraft>
): Array<{ questionId: string; draft: QuestionAnswerDraft }> =>
  caseQuestions.flatMap((question) => {
    const draft = questionAnswerDrafts[question.id];
    if (!draft) return [];

    const hasDraftContent = Boolean(draft.selectedOption?.trim() || draft.freeText?.trim());
    const alreadySaved = Boolean(question.answer?.selectedOption?.trim() || question.answer?.freeText?.trim());

    return hasDraftContent && !alreadySaved ? [{ questionId: question.id, draft }] : [];
  });

const QuestionsTab = ({
  caseQuestions,
  answeredQuestions,
  onGenerateQuestions,
  onContinueToPlan,
  questionAnswerDrafts,
  onUpdateQuestionAnswer,
  autoSaveAnswer,
  onOpenEvidenceDrawer,
  notesDraft,
  onChangeNotesDraft,
  autoSaveNotes
}: QuestionsTabProps) => {
  const freeTextTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const notesTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const draftsRef = useRef(questionAnswerDrafts);
  draftsRef.current = questionAnswerDrafts;

  const [saveStatus, setSaveStatus] = useState<Record<string, "saving" | "saved">>({});
  const [notesSaveStatus, setNotesSaveStatus] = useState<"saving" | "saved" | null>(null);

  useEffect(() => {
    return () => {
      for (const timer of Object.values(freeTextTimers.current)) clearTimeout(timer);
      clearTimeout(notesTimer.current);
    };
  }, []);

  const markQuestionSaved = (questionId: string) => {
    setSaveStatus(prev => ({ ...prev, [questionId]: "saved" }));
    setTimeout(() => setSaveStatus(prev => {
      const { [questionId]: _, ...rest } = prev;
      return rest;
    }), 2500);
  };

  const handleOptionSelect = (questionId: string, option: string) => {
    onUpdateQuestionAnswer(questionId, "selectedOption", option);
    clearTimeout(freeTextTimers.current[questionId]);
    const current = draftsRef.current[questionId];
    const draft = { selectedOption: option, freeText: current?.freeText ?? "" };
    if (option === CUSTOM_QUESTION_OPTION && !draft.freeText.trim()) {
      return;
    }
    setSaveStatus(prev => ({ ...prev, [questionId]: "saving" }));
    void autoSaveAnswer(questionId, draft).then(() => markQuestionSaved(questionId));
  };

  const handleFreeTextChange = (questionId: string, value: string) => {
    onUpdateQuestionAnswer(questionId, "freeText", value);
    clearTimeout(freeTextTimers.current[questionId]);
    freeTextTimers.current[questionId] = setTimeout(() => {
      const current = draftsRef.current[questionId];
      const draft = { selectedOption: current?.selectedOption ?? "", freeText: value };
      setSaveStatus(prev => ({ ...prev, [questionId]: "saving" }));
      void autoSaveAnswer(questionId, draft).then(() => markQuestionSaved(questionId));
    }, 800);
  };

  const handleNotesChange = (value: string) => {
    onChangeNotesDraft(value);
    clearTimeout(notesTimer.current);
    setNotesSaveStatus("saving");
    notesTimer.current = setTimeout(() => {
      void autoSaveNotes(value).then(() => {
        setNotesSaveStatus("saved");
        setTimeout(() => setNotesSaveStatus(null), 2500);
      });
    }, 1000);
  };

  const canProceed = caseQuestions.length > 0 && caseQuestions.every(q => {
    const draft = questionAnswerDrafts[q.id];
    return Boolean(
      q.answer?.selectedOption?.trim() || q.answer?.freeText?.trim() ||
      draft?.selectedOption?.trim() || draft?.freeText?.trim()
    );
  });

  const handleContinueToPlan = async (): Promise<void> => {
    const pendingAnswerSaves = getUnsavedQuestionDrafts(caseQuestions, questionAnswerDrafts)
      .map(({ questionId, draft }) => autoSaveAnswer(questionId, draft));

    await Promise.all([...pendingAnswerSaves, autoSaveNotes(notesDraft)]);
    await onContinueToPlan();
  };

  const totalQuestions = caseQuestions.length;
  const progressPercent = totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Completion banner */}
      {canProceed && totalQuestions > 0 ? (
        <div className="flex items-center justify-between gap-4 rounded-lg border border-emerald-200 bg-emerald-50 p-6 dark:border-emerald-900 dark:bg-emerald-950/30">
          <div className="flex items-center gap-3">
            <div className="flex size-8 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/50">
              <Check className="size-4 text-emerald-700 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-emerald-900 dark:text-emerald-300">All questions answered</p>
              <p className="text-xs text-emerald-700/80 dark:text-emerald-400/70">Ready to generate the proposal outline.</p>
            </div>
          </div>
          <Button size="sm" onClick={() => void handleContinueToPlan()} className="shrink-0">
            Generate Outline
            <ArrowRight className="size-4" />
          </Button>
        </div>
      ) : null}

      {/* Steering notes */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base">Steering Notes</CardTitle>
              <CardDescription>Priorities, constraints, and direction for the AI to follow.</CardDescription>
            </div>
            {notesSaveStatus ? (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                {notesSaveStatus === "saving" ? (
                  <><LoaderCircle className="size-3 animate-spin" /> Saving…</>
                ) : (
                  <><Check className="size-3 text-emerald-600" /> Saved</>
                )}
              </span>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          <Textarea
            id="working-notes"
            className="min-h-36 text-sm leading-relaxed resize-y"
            value={notesDraft}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="E.g. emphasise security expertise, downplay timeline risk, lead with the Aarhus reference…"
          />
        </CardContent>
      </Card>

      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {totalQuestions > 0 ? (
            <div className="flex items-center gap-2.5">
              <Progress value={progressPercent} className="h-2 w-24" />
              <span className="text-sm text-muted-foreground tabular-nums">
                {answeredQuestions}/{totalQuestions} answered
              </span>
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onOpenEvidenceDrawer}>
            <PanelRightOpen className="size-4" />
            Source Docs
          </Button>
          <Button variant="outline" size="sm" onClick={() => void onGenerateQuestions()}>
            <Sparkles className="size-4" />
            Regenerate
          </Button>
          <Button size="sm" onClick={() => void handleContinueToPlan()} disabled={!canProceed}>
            Continue to outline
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </div>

      {/* Questions */}
      {caseQuestions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16">
            <ClipboardCheck className="text-muted-foreground size-10" />
            <div className="text-center">
              <p className="font-medium">No steering questions yet</p>
              <p className="text-muted-foreground mt-1 text-sm">
                Generate questions to identify key decisions for this proposal.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {caseQuestions.map((question) => {
            const draft = questionAnswerDrafts[question.id] ?? buildQuestionAnswerDraft(question);
            const status = saveStatus[question.id];
            const isSaved = hasSavedQuestionAnswer(question.answer);
            const isCustomSelected = draft.selectedOption === CUSTOM_QUESTION_OPTION;
            const answerOptions = [...question.options, CUSTOM_QUESTION_OPTION];

            return (
              <Card key={question.id} className="overflow-hidden">
                <div className="flex">
                  <div className="flex w-11 shrink-0 items-start justify-center border-r bg-muted/30 pt-5">
                    <span className={`flex size-7 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                      isSaved
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400"
                        : "bg-primary/10 text-primary"
                    }`}>
                      {isSaved ? <Check className="size-3.5" /> : question.position + 1}
                    </span>
                  </div>

                  <div className="flex-1 space-y-3 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1 min-w-0">
                        <p className="font-medium leading-snug">{question.text}</p>
                        <p className="text-muted-foreground text-sm leading-relaxed">{question.rationale}</p>
                      </div>
                      {status ? (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0 pt-0.5">
                          {status === "saving" ? (
                            <LoaderCircle className="size-3 animate-spin" />
                          ) : (
                            <Check className="size-3 text-emerald-600" />
                          )}
                          {status === "saving" ? "Saving" : "Saved"}
                        </span>
                      ) : null}
                    </div>

                    <div className="space-y-1">
                      {answerOptions.map((option) => {
                        const isSelected = draft.selectedOption === option;
                        const isSuggested = option !== CUSTOM_QUESTION_OPTION && question.suggestedOption === option;
                        const optionLabel = option === CUSTOM_QUESTION_OPTION ? "Custom" : option;
                        return (
                          <button
                            type="button"
                            key={option}
                            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                              isSelected
                                ? "bg-primary/8 ring-1 ring-primary/20 font-medium"
                                : "hover:bg-muted/60"
                            }`}
                            onClick={() => handleOptionSelect(question.id, option)}
                          >
                            <span className={`flex size-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                              isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"
                            }`}>
                              {isSelected ? <span className="size-1.5 rounded-full bg-white" /> : null}
                            </span>
                            <span className="flex-1">{optionLabel}</span>
                            {isSuggested ? (
                              <Badge variant="outline" className="text-xs shrink-0">Suggested</Badge>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>

                    <Textarea
                      id={`freetext-${question.id}`}
                      className="min-h-20 text-sm resize-y"
                      placeholder={
                        isCustomSelected
                          ? "Write the custom answer that should be used for this question…"
                          : "Add nuance, constraints, or stakeholder preferences…"
                      }
                      value={draft.freeText}
                      onChange={(e) => handleFreeTextChange(question.id, e.target.value)}
                    />
                    {isCustomSelected && !hasQuestionAnswerValue(draft.selectedOption, draft.freeText) ? (
                      <p className="text-muted-foreground text-xs">
                        Enter the custom answer above to complete this question.
                      </p>
                    ) : null}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Plan Tab
// ---------------------------------------------------------------------------

type PlanTabProps = {
  casePlan: PlanRow | null;
  caseReadiness: CaseReadiness;
  outlineNeedsRefresh: boolean;
  planMarkdown: string;
  onChangePlanMarkdown: (value: string) => void;
  onGeneratePlan: () => Promise<void>;
  onSavePlan: (status: "draft" | "approved") => Promise<void>;
  onBackToQuestions: () => void;
  onOpenEvidenceDrawer: () => void;
};

const PlanTab = ({
  casePlan,
  caseReadiness,
  outlineNeedsRefresh,
  planMarkdown,
  onChangePlanMarkdown,
  onGeneratePlan,
  onSavePlan,
  onBackToQuestions,
  onOpenEvidenceDrawer
}: PlanTabProps) => (
  <div className="space-y-4">
    {outlineNeedsRefresh ? (
      <Card className="border-amber-200 bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/20">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div>
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">Steering inputs changed</p>
            <p className="text-sm text-amber-800/80 dark:text-amber-300/80">
              Regenerate the outline to reflect the latest answers and steering notes.
            </p>
          </div>
          <Button size="sm" onClick={() => void onGeneratePlan()} disabled={!caseReadiness.canGeneratePlan}>
            <Sparkles className="size-4" />
            Regenerate outline
          </Button>
        </CardContent>
      </Card>
    ) : null}
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        {casePlan ? (
          <>
            <Badge variant="outline">v{casePlan.version}</Badge>
            <Badge variant="outline" className={getPlanStatusBadgeClassName(casePlan.status)}>
              {getPlanStatusLabel(casePlan.status)}
            </Badge>
            <span className="text-muted-foreground text-sm">Updated {formatTimestamp(casePlan.updated_at)}</span>
          </>
        ) : (
          <span className="text-muted-foreground text-sm">No outline generated yet</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBackToQuestions}>
          <ArrowLeft className="size-4" />
          Back to Questions
        </Button>
        <Button variant="ghost" size="sm" onClick={onOpenEvidenceDrawer}>
          <PanelRightOpen className="size-4" />
          Source Docs
        </Button>
        <Button variant="outline" size="sm" onClick={() => void onGeneratePlan()} disabled={!caseReadiness.canGeneratePlan}>
          <Sparkles className="size-4" />
          {casePlan ? "Regenerate outline" : "Generate outline"}
        </Button>
        <Button variant="outline" size="sm" onClick={() => void onSavePlan("draft")} disabled={!casePlan}>
          <Save className="size-4" />
          Save draft
        </Button>
        <Button size="sm" onClick={() => void onSavePlan("approved")} disabled={!casePlan}>
          <ShieldCheck className="size-4" />
          Approve Outline
        </Button>
      </div>
    </div>

    <div className="space-y-1.5">
      <p className="text-sm font-medium">Editor</p>
      <MarkdownEditor
        value={planMarkdown}
        onChange={onChangePlanMarkdown}
        placeholderText="Use markdown headings and bullets to structure the proposal outline."
      />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Proposal Tab
// ---------------------------------------------------------------------------

type ProposalTabProps = {
  selectedCase: CaseRow | null;
  casePlan: PlanRow | null;
  caseProposals: ProposalRow[];
  proposalNotes: string;
  onChangeProposalNotes: (value: string) => void;
  proposalFeedback: string;
  onChangeProposalFeedback: (value: string) => void;
  onRunProposal: () => Promise<void>;
  onRegeneratePdf: (version: number) => Promise<void>;
  onOpenPdf: (version: number) => void;
  onDownloadPdf: (version: number) => void;
  onDummySend: () => Promise<void>;
  caseReadiness: CaseReadiness;
  onBackToPlan: () => void;
};

const ProposalTab = ({
  selectedCase,
  casePlan,
  caseProposals,
  onRunProposal,
  onRegeneratePdf,
  onOpenPdf,
  onDownloadPdf,
  onDummySend,
  caseReadiness,
  onBackToPlan
}: ProposalTabProps) => (
  <div className="space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        {casePlan ? (
          <Badge variant="outline" className={getPlanStatusBadgeClassName(casePlan.status)}>
            Using outline v{casePlan.version} ({getPlanStatusLabel(casePlan.status)})
          </Badge>
        ) : (
          <span className="text-muted-foreground text-sm">No outline generated yet</span>
        )}
        {selectedCase?.workflow_language ? (
          <Badge variant="secondary">{selectedCase.workflow_language}</Badge>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBackToPlan}>
          <ArrowLeft className="size-4" />
          Back to Outline
        </Button>
        <Button variant="outline" size="sm" onClick={() => void onDummySend()} disabled={caseProposals.length === 0}>
          <Mail className="size-4" />
          Test Submit
        </Button>
        <Button variant="outline" size="sm" onClick={() => void onRunProposal()} disabled={!caseReadiness.canGenerateProposal}>
          <NotebookPen className="size-4" />
          {caseProposals.length > 0 ? "Regenerate proposal" : "Generate proposal"}
        </Button>
      </div>
    </div>

    {/* Primary PDF action when proposals exist */}
    {caseProposals.length > 0 ? (
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex items-center gap-3">
            <FileText className="text-primary size-8" />
            <div>
              <p className="font-medium">Proposal v{caseProposals[0].version}</p>
              <p className="text-muted-foreground text-sm">
                {getJobStatusLabel(caseProposals[0].status)} · {formatTimestamp(caseProposals[0].created_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => onOpenPdf(caseProposals[0].version)}>
              <ExternalLink className="size-4" />
              View PDF
            </Button>
            <Button variant="outline" onClick={() => onDownloadPdf(caseProposals[0].version)}>
              <Download className="size-4" />
              Download
            </Button>
          </div>
        </CardContent>
      </Card>
    ) : null}

    <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <CardTitle>Iteration Flow</CardTitle>
          <CardDescription>Use the Chat page to iterate once the first proposal exists.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>Chat now keeps the full revision transcript, proposal versions, PDF artifacts, and regeneration prompts.</p>
          <p>This tab stays focused on the latest result and version shortcuts.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Latest Draft</CardTitle>
        </CardHeader>
        <CardContent>
          {caseProposals.length > 0 ? (
            (() => {
              const latest = caseProposals[0];
              const hasTypst = latest.typst_source.trim().length > 0;
              return hasTypst ? (
                <pre className="overflow-x-auto rounded-lg border bg-muted/30 p-4 text-sm font-mono whitespace-pre-wrap break-words max-h-[500px] overflow-y-auto">
                  {latest.typst_source}
                </pre>
              ) : (
                <article className="prose prose-sm dark:prose-invert max-w-none max-h-[500px] overflow-y-auto">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{latest.draft_markdown}</ReactMarkdown>
                </article>
              );
            })()
          ) : (
            <div className="flex flex-col items-center gap-3 py-12">
              <NotebookPen className="text-muted-foreground size-10" />
              <div className="text-center">
                <p className="font-medium">No proposal draft yet</p>
                <p className="text-muted-foreground mt-1 text-sm">
                  Generate the first proposal once the outline has been approved.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>

    {caseProposals.length > 1 ? (
      <Card>
        <CardHeader>
          <CardTitle>Version History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y rounded-lg border">
            {caseProposals.map((proposal) => (
              <div key={proposal.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="flex items-center gap-3">
                  <Badge variant="secondary">v{proposal.version}</Badge>
                  <div>
                    <p className="text-sm font-medium">{proposal.language} · Outline v{proposal.plan_version}</p>
                    <p className="text-muted-foreground text-xs">{getJobStatusLabel(proposal.status)} · {formatTimestamp(proposal.created_at)}</p>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => onOpenPdf(proposal.version)}>
                    View PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDownloadPdf(proposal.version)}>
                    Download
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void onRegeneratePdf(proposal.version)}>
                    Regenerate
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    ) : null}
  </div>
);

// ---------------------------------------------------------------------------
// Evidence Tab (full width)
// ---------------------------------------------------------------------------

type EvidenceTabProps = {
  selectedCaseId: string | null;
  importViewerFileTree: FileTreeNode[];
  selectedImportFilePath: string;
  onSelectImportFile: (filePath: string) => void;
  importViewerMarkdownContent: string;
  importViewerError: string;
  isImportViewerLoading: boolean;
};

const EvidenceTab = ({
  selectedCaseId,
  importViewerFileTree,
  selectedImportFilePath,
  onSelectImportFile,
  importViewerMarkdownContent,
  importViewerError,
  isImportViewerLoading
}: EvidenceTabProps) => (
  <Card>
    <CardHeader>
      <CardTitle>Tender Documents</CardTitle>
    </CardHeader>
    <CardContent className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
      <EvidenceBrowser
        importViewerFileTree={importViewerFileTree}
        selectedImportFilePath={selectedImportFilePath}
        onSelectImportFile={onSelectImportFile}
      />
      <EvidencePreview
        selectedCaseId={selectedCaseId}
        selectedImportFilePath={selectedImportFilePath}
        importViewerMarkdownContent={importViewerMarkdownContent}
        importViewerError={importViewerError}
        isImportViewerLoading={isImportViewerLoading}
      />
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// Activity Tab (full width)
// ---------------------------------------------------------------------------

type ActivityTabProps = {
  caseJobs: JobRow[];
  caseLogs: JobLogRow[];
  activityEvents: ActivityEventRow[];
  caseReadiness: CaseReadiness;
  selectedCase: CaseRow | null;
  caseFeasibility: FeasibilityRow | null;
  casePlan: PlanRow | null;
  caseProposals: ProposalRow[];
  selectedImportLogJobId: string | null;
  selectedImportLogFiles: ImportFileRow[];
  onToggleImportLogDetails: (jobId: string) => Promise<void>;
  onGenerateQuestions: () => Promise<void>;
  onOpenQuestions: () => void;
  onGeneratePlan: () => Promise<void>;
  onOpenPlan: () => void;
  onRunProposal: () => Promise<void>;
  proposalChatDraft: string;
  onChangeProposalChatDraft: (value: string) => void;
  onSendProposalChatMessage: () => Promise<void>;
  onOpenPdf: (version: number) => void;
  onDownloadPdf: (version: number) => void;
  isFullscreen?: boolean;
};

type ActivityTimelineItem =
  | {
      type: "event";
      timestamp: string;
      align: "left" | "right";
      event: CombinedActivityEvent;
    }
  | {
      type: "job";
      timestamp: string;
      job: JobRow;
    }
  | {
      type: "feasibility";
      timestamp: string;
      feasibility: FeasibilityRow;
    };

type ActivityTimelineBlock =
  | {
      type: "event_group";
      id: string;
      align: "left" | "right";
      events: CombinedActivityEvent[];
    }
  | {
      type: "job";
      id: string;
      job: JobRow;
    }
  | {
      type: "feasibility";
      id: string;
      feasibility: FeasibilityRow;
    };

const activityJobLabels: Record<string, string> = {
  "import+feasibility": "Import & Feasibility",
  questions: "Steering Questions",
  plan: "Outline",
  "proposal+pdf": "Proposal"
};

const activityStatusTone: Record<string, string> = {
  succeeded: "bg-[#22C153]/12 text-[#0F6B2C] dark:bg-[#22C153]/16 dark:text-[#9EF0B8]",
  failed: "bg-black text-white dark:bg-white/12 dark:text-white",
  running: "bg-[#DEEBED] text-[#24424A] dark:bg-[#DEEBED]/10 dark:text-[#DEEBED]",
  queued: "bg-[#FDA64B]/16 text-[#8A4B00] dark:bg-[#FDA64B]/14 dark:text-[#FFD5AB]"
};

const getActivityJobLabel = (kind: string): string => activityJobLabels[kind] ?? kind;

const getActivityStatusClassName = (status: string): string =>
  activityStatusTone[status] ?? "bg-muted text-muted-foreground";

const getActivityLogTone = (level: JobLogRow["level"]): string =>
  level === "error" ? "bg-red-500" : "bg-slate-400 dark:bg-slate-500";

const ActivityLogList = ({
  logs,
  maxHeightClassName = "max-h-64",
  emptyMessage = "No log entries for this step yet."
}: {
  logs: JobLogRow[];
  maxHeightClassName?: string;
  emptyMessage?: string;
}) => (
  <div className={cn("overflow-y-auto rounded-lg border bg-muted/20", maxHeightClassName)}>
    {logs.length === 0 ? (
      <p className="text-muted-foreground px-3 py-2 text-xs">{emptyMessage}</p>
    ) : (
      <div className="divide-y">
        {logs.map((log) => (
          <div key={log.id} className="grid grid-cols-[auto_auto_minmax(0,1fr)] items-start gap-2 px-3 py-1.5 text-[11px] leading-4">
            <span className="text-muted-foreground font-mono tabular-nums">{formatCompactTimestamp(log.created_at)}</span>
            <span
              className={cn("mt-1 size-1.5 rounded-full", getActivityLogTone(log.level))}
              aria-hidden="true"
            />
            <p className={cn("whitespace-pre-wrap break-words", log.level === "error" ? "text-foreground font-medium" : "text-muted-foreground")}>
              {log.message}
            </p>
          </div>
        ))}
      </div>
    )}
  </div>
);

const JOB_EVENT_MAX_SKEW_MS = 15_000;

const findNearestJobAfterTimestamp = (jobs: JobRow[], timestamp: string): JobRow | null => {
  const eventTime = new Date(timestamp).getTime();
  let nearestJob: JobRow | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (const job of jobs) {
    const createdAt = new Date(job.created_at).getTime();
    if (createdAt < eventTime) {
      continue;
    }

    const distance = createdAt - eventTime;
    if (distance > JOB_EVENT_MAX_SKEW_MS || distance >= nearestDistance) {
      continue;
    }

    nearestDistance = distance;
    nearestJob = job;
  }

  return nearestJob;
};

const findLatestJobBeforeTimestamp = (jobs: JobRow[], timestamp: string): JobRow | null => {
  const eventTime = new Date(timestamp).getTime();
  let latestJob: JobRow | null = null;

  for (const job of jobs) {
    const createdAt = new Date(job.created_at).getTime();
    if (createdAt > eventTime) {
      continue;
    }

    if (!latestJob || createdAt >= new Date(latestJob.created_at).getTime()) {
      latestJob = job;
    }
  }

  return latestJob;
};

const getRelatedJobForEvent = (
  event: CombinedActivityEvent,
  jobsByKind: Map<string, JobRow[]>
): JobRow | null => {
  const metadata = event.metadata;
  const proposalJobs = jobsByKind.get("proposal+pdf") ?? [];
  const planJobs = jobsByKind.get("plan") ?? [];
  const questionJobs = jobsByKind.get("questions") ?? [];

  if (event.kind === "workflow_status") {
    if (event.summary === "Proposal generation started") {
      return findNearestJobAfterTimestamp(proposalJobs, event.createdAt);
    }

    if (typeof metadata.planVersion === "number" || event.summary.startsWith("Generated outline")) {
      return findLatestJobBeforeTimestamp(planJobs, event.createdAt);
    }

    if (typeof metadata.questionCount === "number" || event.summary.includes("steering questions")) {
      return findLatestJobBeforeTimestamp(questionJobs, event.createdAt);
    }
  }

  if (event.kind === "system_notice") {
    if (event.summary === "Proposal generation failed") {
      return findLatestJobBeforeTimestamp(proposalJobs, event.createdAt);
    }

    if (event.summary === "Outline generation failed") {
      return findLatestJobBeforeTimestamp(planJobs, event.createdAt);
    }

    if (event.summary === "Steering question generation failed") {
      return findLatestJobBeforeTimestamp(questionJobs, event.createdAt);
    }
  }

  if (event.kind === "proposal_version" || event.kind === "pdf_artifact" || event.kind === "assistant_message") {
    return findLatestJobBeforeTimestamp(proposalJobs, event.createdAt);
  }

  return null;
};

type ActivityJobCardProps = {
  job: JobRow;
  logs: JobLogRow[];
  selectedImportLogJobId: string | null;
  selectedImportLogFiles: ImportFileRow[];
  onToggleImportLogDetails: (jobId: string) => Promise<void>;
  onOpenDetails: (jobId: string) => void;
};

const ActivityJobCard = ({
  job,
  logs,
  selectedImportLogJobId,
  selectedImportLogFiles,
  onToggleImportLogDetails,
  onOpenDetails
}: ActivityJobCardProps): JSX.Element => (
  <div className="mt-3 rounded-2xl border border-primary/20 bg-background px-4 py-3">
    <div className="flex items-start gap-3">
      <div className={cn("flex size-8 shrink-0 items-center justify-center rounded-full", getActivityStatusClassName(job.status))}>
        {isActiveJobStatus(job.status) ? <LoaderCircle className="size-4 animate-spin" /> : <PanelRightOpen className="size-4" />}
      </div>
      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium">{getActivityJobLabel(job.kind)}</p>
              <Badge variant="outline" className={getJobStatusBadgeClassName(job.status)}>
                {getJobStatusLabel(job.status)}
              </Badge>
              <span className="text-muted-foreground text-xs tabular-nums">{job.progress}%</span>
            </div>
            <p className="text-muted-foreground mt-1 text-xs">
              {formatTimestamp(job.created_at)} · {formatDuration(job.created_at, job.updated_at, job.status)}
            </p>
            {job.message ? <p className="text-muted-foreground mt-2 text-sm">{job.message}</p> : null}
            {job.error ? <p className="text-destructive mt-2 text-sm">{job.error}</p> : null}
          </div>
          <Button size="sm" variant="ghost" onClick={() => onOpenDetails(job.id)}>
            View details
          </Button>
        </div>

        <Progress value={clampProgress(job.progress)} className="h-1.5" />
        <ActivityLogList logs={logs} maxHeightClassName="max-h-40" emptyMessage="Waiting for log entries from this step..." />

        {job.kind === "import+feasibility" ? (
          <div>
            <Button size="sm" variant="ghost" onClick={() => void onToggleImportLogDetails(job.id)}>
              {selectedImportLogJobId === job.id ? "Hide file details" : "Show file details"}
            </Button>
            {selectedImportLogJobId === job.id ? (
              <div className="mt-3 overflow-hidden rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>File</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedImportLogFiles.map((file) => (
                      <TableRow key={file.id}>
                        <TableCell className="font-mono text-xs">{file.relative_path}</TableCell>
                        <TableCell className="text-xs">
                          <span className={importStatusBadgeClass(file.status)}>
                            {getImportStatusLabel(file.status)}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">{file.reason ?? "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  </div>
);

const ActivityTab = ({
  caseJobs,
  caseLogs,
  activityEvents,
  caseReadiness,
  selectedCase,
  caseFeasibility,
  casePlan,
  caseProposals,
  selectedImportLogJobId,
  selectedImportLogFiles,
  onToggleImportLogDetails,
  onGenerateQuestions,
  onOpenQuestions,
  onGeneratePlan,
  onOpenPlan,
  onRunProposal,
  proposalChatDraft,
  onChangeProposalChatDraft,
  onSendProposalChatMessage,
  onOpenPdf,
  onDownloadPdf,
  isFullscreen = false
}: ActivityTabProps) => {
  const { logsByJobId, ungroupedLogs } = useMemo(() => groupJobLogsByJobId(caseLogs), [caseLogs]);
  const [selectedActivityJobId, setSelectedActivityJobId] = useState<string | null>(null);
  const [composerHeight, setComposerHeight] = useState(0);
  const transcriptScrollRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLDivElement | null>(null);

  const successfulProposals = caseProposals.filter((proposal) => proposal.status === "succeeded");
  const canChat = successfulProposals.length > 0;
  const runningJobs = caseJobs.filter((job) => isActiveJobStatus(job.status));
  const jobsByKind = useMemo(
    () => {
      const groupedJobs = new Map<string, JobRow[]>();
      const sorted = [...caseJobs].sort((left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime());
      for (const job of sorted) {
        const existingJobs = groupedJobs.get(job.kind) ?? [];
        existingJobs.push(job);
        groupedJobs.set(job.kind, existingJobs);
      }
      return groupedJobs;
    },
    [caseJobs]
  );

  const combinedEvents = useMemo(() => buildActivityTranscriptEvents(activityEvents, caseJobs), [activityEvents, caseJobs]);
  const latestDedupedCtaEventIds = useMemo(() => findLatestDedupedCtaEventIds(combinedEvents), [combinedEvents]);
  const relatedJobsByEventId = useMemo(() => {
    const relatedJobs = new Map<string, JobRow>();

    for (const event of combinedEvents) {
      const relatedJob = getRelatedJobForEvent(event, jobsByKind);
      if (relatedJob) {
        relatedJobs.set(event.id, relatedJob);
      }
    }

    return relatedJobs;
  }, [combinedEvents, jobsByKind]);
  const inlineJobEventIds = useMemo(() => {
    const eventIds = new Set<string>();
    const matchedJobIds = new Set<string>();

    for (const event of combinedEvents) {
      const relatedJob = relatedJobsByEventId.get(event.id);
      if (!relatedJob || matchedJobIds.has(relatedJob.id)) {
        continue;
      }

      matchedJobIds.add(relatedJob.id);
      eventIds.add(event.id);
    }

    return eventIds;
  }, [combinedEvents, relatedJobsByEventId]);
  const inlineJobIds = useMemo(() => {
    const jobIds = new Set<string>();

    for (const eventId of inlineJobEventIds) {
      const relatedJob = relatedJobsByEventId.get(eventId);
      if (relatedJob) {
        jobIds.add(relatedJob.id);
      }
    }

    return jobIds;
  }, [inlineJobEventIds, relatedJobsByEventId]);
  const standaloneJobs = useMemo(() => getStandaloneActivityJobs(caseJobs, inlineJobIds), [caseJobs, inlineJobIds]);

  const renderEventCta = (event: CombinedActivityEvent): JSX.Element | null => {
    const metadata = event.metadata;
    const cta = metadata.cta as { action?: string; version?: number } | null | undefined;
    if (!cta?.action) {
      return null;
    }

    const shouldDeduplicateAction = cta.action === "generate_plan" || cta.action === "run_proposal";
    if (shouldDeduplicateAction && !latestDedupedCtaEventIds.has(event.id)) {
      return null;
    }

    if (cta.action === "generate_plan") {
      return (
        <Button size="sm" variant="outline" onClick={() => void onGeneratePlan()} disabled={!caseReadiness.canGeneratePlan}>
          {casePlan ? "Regenerate outline" : "Generate outline"}
        </Button>
      );
    }

    if (cta.action === "run_proposal") {
      return (
        <Button size="sm" variant="outline" onClick={() => void onRunProposal()} disabled={!caseReadiness.canGenerateProposal}>
          Regenerate proposal
        </Button>
      );
    }

    if (cta.action === "open_pdf" && typeof cta.version === "number") {
      const version = cta.version;
      return (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onOpenPdf(version)}>
            Open PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDownloadPdf(version)}>
            Download
          </Button>
        </div>
      );
    }

    return null;
  };

  const timelineBlocks = useMemo(() => {
    const timelineItems: ActivityTimelineItem[] = [
      ...combinedEvents.map((event) => ({
        type: "event" as const,
        timestamp: new Date(
          getActivityEventDisplayTime(event, relatedJobsByEventId.get(event.id) ?? null)
        ).toISOString(),
        align: event.actor === "user" || event.kind === "user_change" ? "right" as const : "left" as const,
        event
      })),
      ...standaloneJobs.map((job) => ({
        type: "job" as const,
        timestamp: job.created_at,
        job
      })),
      ...(caseFeasibility
        ? [{
            type: "feasibility" as const,
            timestamp: caseFeasibility.created_at,
            feasibility: caseFeasibility
          }]
        : [])
    ].sort((left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime());

    const blocks: ActivityTimelineBlock[] = [];

    for (const item of timelineItems) {
      if (item.type !== "event") {
        blocks.push(
          item.type === "job"
            ? { type: "job", id: `job:${item.job.id}`, job: item.job }
            : { type: "feasibility", id: `feasibility:${item.feasibility.created_at}`, feasibility: item.feasibility }
        );
        continue;
      }

      const current = blocks.at(-1);
      if (current?.type === "event_group" && current.align === item.align) {
        current.events.push(item.event);
        continue;
      }

      blocks.push({
        type: "event_group",
        id: item.event.id,
        align: item.align,
        events: [item.event]
      });
    }

    return blocks;
  }, [caseFeasibility, combinedEvents, relatedJobsByEventId, standaloneJobs]);

  useEffect(() => {
    const container = transcriptScrollRef.current;
    if (!container) {
      return;
    }

    container.scrollTop = container.scrollHeight;
  }, [combinedEvents.length, runningJobs.length, caseLogs.length]);

  useEffect(() => {
    const composerElement = composerRef.current;
    if (!composerElement) {
      return;
    }

    const updateComposerHeight = () => {
      setComposerHeight(composerElement.getBoundingClientRect().height);
    };

    updateComposerHeight();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateComposerHeight();
    });

    resizeObserver.observe(composerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const renderBottomCtas = (): JSX.Element => {
    const state = selectedCase?.state ?? "";

    if (state === "context_ready") {
      return <Button onClick={() => void onGenerateQuestions()}>Generate questions</Button>;
    }

    if (state === "questions_generated" && selectedCase?.sub_state === "awaiting_answers") {
      return <Button onClick={onOpenQuestions}>Answer questions</Button>;
    }

    if (state === "questions_generated") {
      return (
        <Button onClick={() => void onGeneratePlan()} disabled={!caseReadiness.canGeneratePlan}>
          Generate outline
        </Button>
      );
    }

    if (state === "plan_generated") {
      return <Button onClick={onOpenPlan}>Open Outline</Button>;
    }

    return (
      <Button onClick={() => void onRunProposal()} disabled={!caseReadiness.canGenerateProposal}>
        Generate proposal
      </Button>
    );
  };
  const hasTranscriptContent = timelineBlocks.length > 0 || ungroupedLogs.length > 0;

  return (
    <div className={cn("space-y-4", isFullscreen && "relative flex h-full min-h-0 flex-col")}>
        <div
          data-testid="activity-transcript"
          ref={transcriptScrollRef}
          style={isFullscreen ? { paddingBottom: `${composerHeight + 40}px` } : undefined}
          className={cn(
            "space-y-3 overflow-y-auto rounded-2xl border bg-muted/10 p-3",
            isFullscreen ? "min-h-0 flex-1" : "max-h-[70vh]"
          )}
        >
          {!hasTranscriptContent ? (
            <div className="rounded-xl border border-dashed bg-background px-4 py-10 text-center">
              <p className="font-medium">No transcript entries yet</p>
              <p className="text-muted-foreground mt-1 text-sm">Workflow changes, proposal versions, and chat will appear here.</p>
            </div>
          ) : (
            timelineBlocks.map((block) => {
              if (block.type === "job") {
                const jobLogs = logsByJobId.get(block.job.id) ?? [];
                return (
                  <ActivityJobCard
                    key={block.id}
                    job={block.job}
                    logs={jobLogs}
                    selectedImportLogJobId={selectedImportLogJobId}
                    selectedImportLogFiles={selectedImportLogFiles}
                    onToggleImportLogDetails={onToggleImportLogDetails}
                    onOpenDetails={setSelectedActivityJobId}
                  />
                );
              }

              if (block.type === "feasibility") {
                return (
                  <div key={block.id} className="mr-auto max-w-[85%]">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.12em]">
                          import summary
                        </Badge>
                        <span className="text-muted-foreground text-xs">{formatTimestamp(block.feasibility.created_at)}</span>
                      </div>
                      <FeasibilityInsightPanel caseFeasibility={block.feasibility} className="shadow-none" compact />
                    </div>
                  </div>
                );
              }

              const isBubble = block.align === "right";
              return (
                <div
                  key={block.id}
                  className={cn(
                    "w-full",
                    isBubble
                      ? "ml-auto max-w-[85%] rounded-2xl bg-primary px-4 py-3 text-primary-foreground"
                      : "mr-auto max-w-[85%]"
                  )}
                >
                  {block.events.map((event, index) => {
                    const metadata = event.metadata;
                    const relatedJob = relatedJobsByEventId.get(event.id) ?? null;
                    return (
                      <div key={event.id} className={cn(index > 0 && "mt-3 border-t border-current/10 pt-3")}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {!isBubble ? (
                              <Badge variant="outline" className="text-[10px] uppercase tracking-[0.12em]">
                                {event.kind.replaceAll("_", " ")}
                              </Badge>
                            ) : null}
                            <span className={cn("text-xs", isBubble ? "text-primary-foreground/80" : "text-muted-foreground")}>
                              {formatTimestamp(event.createdAt)}
                            </span>
                          </div>
                          {event.kind === "proposal_version" ? <Badge variant="secondary">v{String(metadata.version ?? "")}</Badge> : null}
                        </div>
                        <p className={cn("mt-2 whitespace-pre-wrap text-sm", isBubble ? "text-primary-foreground" : "text-foreground")}>
                          {event.summary}
                        </p>
                        {event.kind === "pdf_artifact" && typeof metadata.version === "number" ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            <Button size="sm" onClick={() => onOpenPdf(metadata.version as number)}>
                              Open PDF
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => onDownloadPdf(metadata.version as number)}>
                              Download
                            </Button>
                            {relatedJob ? (
                              <Button size="sm" variant="ghost" onClick={() => setSelectedActivityJobId(relatedJob.id)}>
                                View details
                              </Button>
                            ) : null}
                          </div>
                        ) : null}
                        {!isBubble ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {renderEventCta(event)}
                            {relatedJob ? (
                              <Button size="sm" variant="ghost" onClick={() => setSelectedActivityJobId(relatedJob.id)}>
                                View details
                              </Button>
                            ) : null}
                          </div>
                        ) : null}
                        {!isBubble && relatedJob && inlineJobEventIds.has(event.id) ? (
                          <ActivityJobCard
                            job={relatedJob}
                            logs={logsByJobId.get(relatedJob.id) ?? []}
                            selectedImportLogJobId={selectedImportLogJobId}
                            selectedImportLogFiles={selectedImportLogFiles}
                            onToggleImportLogDetails={onToggleImportLogDetails}
                            onOpenDetails={setSelectedActivityJobId}
                          />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}

          {ungroupedLogs.length > 0 ? (
            <div className="rounded-2xl border border-dashed bg-background px-4 py-3">
              <p className="font-medium">General activity</p>
              <div className="mt-3">
                <ActivityLogList logs={ungroupedLogs} />
              </div>
            </div>
          ) : null}

          {!canChat && runningJobs.length === 0 ? (
            <div className="rounded-2xl border border-dashed bg-background px-4 py-3">
              <p className="font-medium">Next step</p>
              <div className="mt-3 flex flex-wrap gap-2">{renderBottomCtas()}</div>
            </div>
          ) : null}
        </div>

        <div
          ref={composerRef}
          className={cn(
            "rounded-2xl border bg-background/95 p-3 backdrop-blur",
            isFullscreen
              ? "absolute right-0 bottom-0 left-0 mx-4 mb-4 sm:mx-6 sm:mb-6"
              : "sticky bottom-0"
          )}
        >
          <div className="flex items-end gap-3">
            <Textarea
              className="min-h-0 flex-1 resize-none rounded-[calc(100%/2)] border-0 bg-muted/50 px-4 py-3 shadow-none focus-visible:ring-1"
              placeholder="Give directions for improving the proposal"
              value={proposalChatDraft}
              onChange={(event) => onChangeProposalChatDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" || event.shiftKey) {
                  return;
                }

                event.preventDefault();
                if (!canChat || !proposalChatDraft.trim()) {
                  return;
                }
                void onSendProposalChatMessage();
              }}
              disabled={!canChat}
            />
            <Button
              className="size-11 rounded-full p-0"
              onClick={() => void onSendProposalChatMessage()}
              disabled={!canChat || !proposalChatDraft.trim()}
              aria-label="Send proposal iteration message"
            >
              <ArrowUp className="size-4" />
            </Button>
          </div>
        </div>
      

      <Dialog open={selectedActivityJobId !== null} onOpenChange={(open) => !open && setSelectedActivityJobId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Underlying details</DialogTitle>
            <DialogDescription>Live and historical job output from the workflow run.</DialogDescription>
          </DialogHeader>
          {selectedActivityJobId ? (
            <div className="space-y-4">
              <ActivityLogList
                logs={logsByJobId.get(selectedActivityJobId) ?? []}
                maxHeightClassName="max-h-[60vh]"
                emptyMessage="No log entries for this workflow."
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Evidence Browser & Preview (shared components)
// ---------------------------------------------------------------------------

type EvidenceBrowserProps = {
  importViewerFileTree: FileTreeNode[];
  selectedImportFilePath: string;
  onSelectImportFile: (filePath: string) => void;
};

const EvidenceBrowser = ({
  importViewerFileTree,
  selectedImportFilePath,
  onSelectImportFile
}: EvidenceBrowserProps) => (
  <div>
    <p className="mb-2 text-sm font-medium">Files</p>
    {importViewerFileTree.length === 0 ? (
      <p className="text-muted-foreground text-sm">No files imported yet.</p>
    ) : (
      <div className="max-h-[70vh] overflow-y-auto rounded-lg border bg-muted/15 p-2">
        <FileTree
          nodes={importViewerFileTree}
          selectedImportFilePath={selectedImportFilePath}
          onSelectImportFile={onSelectImportFile}
        />
      </div>
    )}
  </div>
);

type EvidencePreviewProps = {
  selectedCaseId: string | null;
  selectedImportFilePath: string;
  importViewerMarkdownContent: string;
  importViewerError: string;
  isImportViewerLoading: boolean;
};

const EvidencePreview = ({
  selectedCaseId,
  selectedImportFilePath,
  importViewerMarkdownContent,
  importViewerError,
  isImportViewerLoading
}: EvidencePreviewProps) => {
  const fileUrl =
    selectedCaseId && selectedImportFilePath
      ? `/api/cases/${selectedCaseId}/import-file?path=${encodeURIComponent(selectedImportFilePath)}`
      : "";
  const isMarkdownFile = selectedImportFilePath.endsWith(".md") || selectedImportFilePath.endsWith(".markdown");
  const isImageFile = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(selectedImportFilePath);

  return (
    <div className="rounded-lg border p-4">
      {selectedImportFilePath ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="truncate text-sm font-medium">{selectedImportFilePath}</p>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground inline-flex shrink-0 items-center gap-1 text-xs underline underline-offset-2"
            >
              Open raw
              <ChevronRight className="size-3" />
            </a>
          </div>
          {isMarkdownFile ? (
            isImportViewerLoading ? (
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <LoaderCircle className="size-4 animate-spin" />
                Loading...
              </div>
            ) : importViewerError ? (
              <p className="text-destructive text-sm">{importViewerError}</p>
            ) : (
              <article className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{importViewerMarkdownContent}</ReactMarkdown>
              </article>
            )
          ) : isImageFile ? (
            <img
              src={fileUrl}
              alt={selectedImportFilePath}
              className="max-h-[70vh] w-auto max-w-full rounded-lg border object-contain"
            />
          ) : (
            <p className="text-muted-foreground text-sm">This file type can't be previewed inline.</p>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 py-12">
          <Eye className="text-muted-foreground size-8" />
          <p className="text-muted-foreground text-sm">Select a file to preview</p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// FileTree
// ---------------------------------------------------------------------------

type FileTreeProps = {
  nodes: FileTreeNode[];
  selectedImportFilePath: string;
  onSelectImportFile: (filePath: string) => void;
  depth?: number;
  parentKey?: string;
};

const FileTree = ({
  nodes,
  selectedImportFilePath,
  onSelectImportFile,
  depth = 0,
  parentKey = "root"
}: FileTreeProps): JSX.Element => (
  <div className={depth > 0 ? "ml-2.5 border-l border-border/50 pl-2" : "space-y-0.5"}>
    {nodes.map((node) => {
      const isDirectory = node.path === null;
      const nodeKey = `${parentKey}/${node.name}`;

      if (isDirectory) {
        return (
          <details key={`dir:${nodeKey}`} open className="py-px">
            <summary className="cursor-pointer rounded px-1 py-0.5 text-sm font-medium marker:text-muted-foreground hover:bg-muted/30">
              {node.name}
            </summary>
            <FileTree
              nodes={node.children}
              selectedImportFilePath={selectedImportFilePath}
              onSelectImportFile={onSelectImportFile}
              depth={depth + 1}
              parentKey={nodeKey}
            />
          </details>
        );
      }

      if (!node.path) {
        return <></>;
      }

      const filePath = node.path;
      const isSelected = selectedImportFilePath === filePath;

      return (
        <button
          type="button"
          key={`file:${filePath}`}
          className={`block w-full rounded-md px-1.5 py-0.5 text-left text-sm leading-5 transition-colors ${
            isSelected ? "bg-primary text-primary-foreground" : "hover:bg-muted/60"
          }`}
          onClick={() => onSelectImportFile(filePath)}
        >
          {node.name}
        </button>
      );
    })}
  </div>
);
